import { SyncOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Flex,
  message,
  Row,
  TableProps,
  TabsProps,
  Typography,
} from 'antd';
import { AnyObject } from 'antd/es/_util/type';
import { SorterResult } from 'antd/es/table/interface';
import AlertBar from 'components/AlertBar';
import HeaderCard from 'components/HeaderCard';
import MultiSelect from 'components/MultiSelect';
import ProtectedResource from 'components/RoleBasedAccessControl/ProtectedResource';
import SearchBox from 'components/SearchBox';
import RBCountryFilter, {
  useRoleBasedCountryFilter,
} from 'components/UniversalFilters/RBCountryFilter';
import useAgronomistList from 'hooks/useAgronomistList';
import useFGLList from 'hooks/useFGLList';
import usePaddyBuyerList from 'hooks/usePaddyBuyerList';
import useSubDistrictList from 'hooks/useSubDistrictList';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { routes } from 'routes/AllRoutes';
import { FarmerGroupLeader, PaddyBuyer } from 'types/common';
import { getStakeholderTypeForCountry } from 'utils/helpers';
import testId from 'utils/testId';
import StakeholderDetailDrawer from './components/drawer/StakeholderDetailDrawer';
import StakeholderTabs from './components/StakeholderTabs';
import FarmerGroupLeaderTable from './components/table/FarmerGroupLeaderTable';
import MachineOwnerTable from './components/table/MachineOwnerTable';
import PaddyBuyerTable from './components/table/PaddyBuyerTable';
import UploadModal from './components/UploadModal';
import { getStakeholderProperties, STAKEHOLDER_STATUS_OPTIONS } from './helper';
import useFGLById from './hooks/queries/useFGLById';
import useMachineOwnerList, {
  MachineOwner,
} from './hooks/queries/useMachineOwnerQuery';
import usePaddyBuyerById from './hooks/queries/usePaddyBuyerById';
import usePaddyBuyerSubDistrictList from './hooks/queries/usePaddyBuyerSubDistrictList';
import useSyncRecords from './hooks/mutations/useSyncRecords';
import './mobile.scss';
import './style.scss';
import useMachineOwnerById from './hooks/queries/useMachineOwnerById';

const keyToSort: Record<string, string> = {
  name: 'name',
  bankName: 'bankAccountInfos_bankName',
  srAgronomistEmail: 'agronomist_email',
  district: 'address_district',
  subDistrict: 'address_subDistrict',
};

type StakeholderTabsKeys = '1' | '2' | '3';

const Stakeholders = () => {
  // Table Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortedInfo, setSortedInfo] = useState<
    SorterResult<PaddyBuyer | FarmerGroupLeader | MachineOwner | AnyObject>
  >({});
  const [sort, setSort] = useState('');
  // Table States
  const [selectedRow, setSelectedRow] = useState<
    PaddyBuyer | FarmerGroupLeader | MachineOwner
  >();
  const [error] = useState('');
  // Filters
  const [search, setSearch] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [agronomist, setAgronomist] = useState<string[]>([]);
  const [subDistrict, setSubDistrict] = useState<string[]>([]);
  const [stakeholderStatus, setStakeholderStatus] = useState<string[]>([]);
  // Tabs
  const [activeTab, setActiveTab] = useState<StakeholderTabsKeys>('1');
  // Drawer
  const [isOpenDetail, setIsOpenDetail] = useState(false);
  const [isOpenFarmerGroupDetail, setIsOpenFarmerGroupDetail] = useState(false);
  // Modal
  const [isOpenUpload, setIsOpenUpload] = useState(false);
  const [failedUrl, setFailedUrl] = useState('');
  // Hooks
  const [country, setCountry] = useRoleBasedCountryFilter();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  // Variables
  const paddyBuyerId = searchParams.get('paddyBuyerId') || undefined;
  const leaderId = searchParams.get('leaderId') || undefined;
  const machineOwnerId = searchParams.get('machineOwnerId') || undefined;
  const { searchPlaceholder } = getStakeholderProperties(
    location.pathname,
    country,
  );
  const isPaddyBuyer =
    activeTab === '1' || location.pathname === routes.paddyBuyer;
  const params = useMemo(
    () => ({
      size: pageSize,
      page: currentPage - 1,
      search,
      subDistrict: subDistrict.join(','),
      status: stakeholderStatus.join(','),
      ...(isPaddyBuyer ? { srAg: agronomist?.join(',') } : {}),
      sort,
      country,
    }),
    [
      pageSize,
      currentPage,
      search,
      agronomist,
      isPaddyBuyer,
      subDistrict,
      sort,
      country,
      stakeholderStatus,
    ],
  );
  // Queries / Mutations
  const { subDistrictOptions, isLoading: isSubDistrictLoading } =
    useSubDistrictList(country);

  const {
    paddyBuyerSubDistrictOptions,
    isLoading: isPaddyBuyerSubDistrictLoading,
  } = usePaddyBuyerSubDistrictList(country);

  const {
    data: fglData,
    isLoading: isFGLLoading,
    refetch: refetchFGL,
    totalCount: fglTotalCount,
  } = useFGLList(
    params,
    location.pathname === routes.farmerGroupLeader && !!country,
  );

  const { agronomistFilterOptions, isLoading: isAgronomistLoading } =
    useAgronomistList(country, {
      role: 'sr',
      labelKey: 'email',
      valueKey: 'email',
    });

  const {
    data: paddyBuyerData,
    isLoading: isPaddyBuyerLoading,
    refetch: refetchPaddyBuyer,
    totalCount: paddyBuyerTotalCount,
  } = usePaddyBuyerList(params, isPaddyBuyer && !!country);

  const { data: { data: paddyBuyerById } = {} } =
    usePaddyBuyerById(paddyBuyerId);
  const { data: { data: leaderById } = {} } = useFGLById(leaderId);

  const { mutateAsync: syncRecords, isPending: isSyncPending } =
    useSyncRecords();

  const {
    data: machineOwnerData,
    isLoading: isMachineOwnerLoading,
    refetch: refetchMachineOwner,
  } = useMachineOwnerList(params, location.pathname === routes.machineOwner);

  const { data: { data: machineOwnerById } = {} } = useMachineOwnerById(
    machineOwnerId,
    location.pathname === routes.machineOwner,
  );

  // Functions
  const refreshList = () => {
    switch (location.pathname) {
      case routes.paddyBuyer:
      case routes.stakeholders:
      default:
        refetchPaddyBuyer();
        break;
      case routes.farmerGroupLeader:
        refetchFGL();
        break;
      case routes.machineOwner:
        refetchMachineOwner();
        break;
    }
  };

  const resetParams = () => {
    setSearch('');
    setSearchValue('');
    setSort('');
    setSortedInfo({});
    setAgronomist([]);
    setSubDistrict([]);
    setStakeholderStatus([]);
    setCurrentPage(1);
  };

  const onTabChange = () => {
    resetParams();
    const type = isPaddyBuyer ? 'PaddyBuyer' : 'FarmerGroupLeader';
    const url = localStorage.getItem(`${type}_Failed_Data_URL`) || '';
    setFailedUrl(url);
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  const handleChange: TableProps['onChange'] = (_, __, tableSort) => {
    const sortResult = Array.isArray(tableSort) ? tableSort[0] : tableSort;
    if (sortResult.order) {
      setSort(
        `${keyToSort[sortResult.columnKey as string]},${
          sortResult.order === 'ascend' ? 'asc' : 'desc'
        }`,
      );
      setSortedInfo(sortResult);
    } else {
      setSort('');
      setSortedInfo({});
    }
  };

  const handleAction = (
    data: PaddyBuyer | FarmerGroupLeader | MachineOwner,
  ) => {
    setSelectedRow(data);
    setIsOpenDetail(true);
  };

  const onCloseDrawer = () => {
    if (isOpenFarmerGroupDetail) {
      setIsOpenFarmerGroupDetail(false);
    } else {
      setIsOpenDetail(false);
      setSelectedRow(undefined);
    }
  };

  const handleSync = async () => {
    if (country !== 'Indonesia') {
      message.error({
        content: t('stakeholders.syncUnavailableForCountry'),
        duration: 3,
      });
      return;
    }
    const data = await syncRecords(country, {
      onError: () => {
        message.error({
          content: t('stakeholders.syncFailed'),
          duration: 3,
        });
      },
    });

    const failureCount = data?.filter((d) => Boolean(d?.failureReason))?.length;
    const successCount = data?.length - failureCount;

    if (successCount > 0) {
      message.destroy();
      message.success({
        content: (
          <Flex vertical gap={8} className="success-message">
            <Typography.Title level={4}>
              {t('stakeholders.syncSuccess')}
            </Typography.Title>
            <p>
              {t('stakeholders.countOfStakeholders', { count: successCount })}
            </p>
          </Flex>
        ),
        duration: 3,
        className: 'sync-message',
      });
      refreshList();
    }

    if (failureCount > 0) {
      message.error({
        content: (
          <Flex vertical gap={8} className="error-message">
            <Typography.Title level={4}>
              {t('stakeholders.syncFailed')}
            </Typography.Title>
            <p>
              {t('stakeholders.countOfFailedStakeholders', {
                count: failureCount,
              })}
            </p>
          </Flex>
        ),
        duration: 3,
        className: 'sync-message',
      });
    }
  };

  const tabsItems: TabsProps['items'] = [
    {
      key: '1',
      label: (
        <span
          data-testId={
            testId.stakeholderPage.tabs['btn-stakeholder-tab-paddy_buyer']
          }
        >
          {getStakeholderTypeForCountry(country, 'PB')}
        </span>
      ),

      children: (
        <PaddyBuyerTable
          data={paddyBuyerData?.data}
          totalData={paddyBuyerTotalCount}
          selectedRow={selectedRow as PaddyBuyer}
          isLoading={isPaddyBuyerLoading}
          pageSize={pageSize}
          currentPage={currentPage}
          sortedInfo={sortedInfo as SorterResult<PaddyBuyer>}
          handlePageChange={handlePageChange}
          handleAction={handleAction}
          handleChange={handleChange as TableProps<PaddyBuyer>['onChange']}
        />
      ),
    },
    {
      key: '2',
      label: (
        <span
          data-testId={
            testId.stakeholderPage.tabs[
              'btn-stakeholder-tab-farmer_group_leader'
            ]
          }
        >
          {t('stakeholders.farmerGroupLeader')}
        </span>
      ),

      children: (
        <FarmerGroupLeaderTable
          data={fglData?.data?.content}
          selectedRow={selectedRow as FarmerGroupLeader}
          totalData={fglTotalCount}
          isLoading={isFGLLoading}
          pageSize={pageSize}
          currentPage={currentPage}
          sortedInfo={sortedInfo as SorterResult<FarmerGroupLeader>}
          handlePageChange={handlePageChange}
          handleAction={handleAction}
          handleChange={
            handleChange as TableProps<FarmerGroupLeader>['onChange']
          }
        />
      ),
    },
    {
      key: '3',
      label: (
        <span
          data-testId={
            testId.stakeholderPage.tabs['btn-stakeholder-tab-machine_owner']
          }
        >
          {t('stakeholders.machineOwner')}
        </span>
      ),
      children: (
        <MachineOwnerTable
          data={machineOwnerData?.data?.content}
          totalData={machineOwnerData?.data?.totalElements ?? 0}
          selectedRow={selectedRow as MachineOwner}
          isLoading={isMachineOwnerLoading}
          pageSize={pageSize}
          currentPage={currentPage}
          handlePageChange={handlePageChange}
          handleAction={handleAction}
          handleChange={handleChange as TableProps<MachineOwner>['onChange']}
        />
      ),
    },
  ];

  useEffect(() => {
    if (paddyBuyerId && paddyBuyerById) {
      setSelectedRow(paddyBuyerById);
      setIsOpenDetail(true);
    }
    if (leaderId && leaderById) {
      setSelectedRow(leaderById);
      setIsOpenDetail(true);
    }
    if (machineOwnerId && machineOwnerById) {
      setSelectedRow(machineOwnerById);
      setIsOpenDetail(true);
    }
  }, [
    paddyBuyerId,
    leaderId,
    paddyBuyerById,
    leaderById,
    machineOwnerId,
    machineOwnerById,
  ]);

  return (
    <div className="dashboard-container">
      {error && <AlertBar type="error" message={error} />}
      <HeaderCard
        headerLabel={t('stakeholders.farmerStakeholdersManagement')}
        headerInfo={t('stakeholders.headerDescription', {
          stakeholder1: getStakeholderTypeForCountry(
            country,
            'PB',
          )?.toLowerCase(),
        })}
        filters={
          <Flex gap={20} align="center" wrap="wrap">
            <RBCountryFilter selectedCountry={country} onChange={setCountry} />
          </Flex>
        }
      />

      <Row>
        <Col span={24} className="table-container">
          <Flex align={'center'} wrap="wrap" gap={20} className="w-full mb-4">
            <Flex gap={20} wrap="wrap">
              <SearchBox
                placeholder={searchPlaceholder}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                onSearch={(value) => {
                  setSearch(value);
                  setSearchValue(value);
                }}
              />
              <MultiSelect
                label={t('common.filters.subDistrict')}
                placeholder={t('common.filters.selectSubDistrict')}
                options={
                  isPaddyBuyer
                    ? paddyBuyerSubDistrictOptions
                    : subDistrictOptions
                }
                value={subDistrict}
                onChange={setSubDistrict}
                disabled={
                  isSubDistrictLoading || isPaddyBuyerSubDistrictLoading
                }
                allowClear
              />

              <MultiSelect
                label={t('common.filters.status')}
                placeholder={t('common.filters.statusPlaceholder')}
                options={STAKEHOLDER_STATUS_OPTIONS}
                value={stakeholderStatus}
                onChange={setStakeholderStatus}
                disabled={isPaddyBuyerLoading}
                allowClear
              />

              {isPaddyBuyer && (
                <ProtectedResource permission="user:read">
                  <MultiSelect
                    label={t('common.filters.srAgronomist')}
                    placeholder={t('common.filters.srAgronomistPlaceholder')}
                    options={agronomistFilterOptions}
                    value={agronomist}
                    onChange={setAgronomist}
                    disabled={isAgronomistLoading}
                    allowClear
                  />
                </ProtectedResource>
              )}
            </Flex>

            <Button
              className="ms-md-auto"
              icon={<SyncOutlined spin={isSyncPending} />}
              type="primary"
              onClick={handleSync}
              loading={isSyncPending}
              disabled={country !== 'Indonesia'}
              data-testId={testId.stakeholderPage['btn-stakeholder-sync-deals']}
            >
              {t('common.syncDeals')}
            </Button>
          </Flex>
          <StakeholderTabs
            country={country}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            tabsItems={tabsItems}
            onTabChange={onTabChange}
            onClickUpload={() => setIsOpenUpload(true)}
            downloadUrl={failedUrl}
          />
        </Col>
      </Row>
      <UploadModal
        isOpen={isOpenUpload}
        setIsOpen={setIsOpenUpload}
        okText={t('genericForm.uploadFile')}
        isPaddyBuyer={isPaddyBuyer}
        setFailedUrl={setFailedUrl}
        onSuccess={refreshList}
      />
      <StakeholderDetailDrawer
        country={country}
        data={selectedRow}
        open={isOpenDetail}
        onClose={onCloseDrawer}
        isOpenFarmerGroupDetail={isOpenFarmerGroupDetail}
        setIsOpenFarmerGroupDetail={setIsOpenFarmerGroupDetail}
      />
    </div>
  );
};

export default Stakeholders;
